/**
 * a simple collection of application configuration flags to be turned
 * into potential state based configuration option in a few months
 */

// try to keep it simple. preferably booleans.
// add a description of what the expected feature will/will-not do
export type ApplicationConfigurationFlags = {
    // when true, should make the app select the most recent chapter when a new fireId is activated in the application state
    autoSelectRecentChapter: boolean

    // true - should enable the controls to access the keppler hex map type
    hexMapFeature: boolean

    // true - should provide user with the controls to initiate the raw data export of layers (perimeter, hotspots etc)
    rawDataExportFeature: boolean

    // true - should toggle all the controls to allow user to show the orthomosaic map view
    orthomosaicFeature: boolean

    // true - enables any features related to managing flight path
    flightPathFeature: boolean

    // true - enables toggling hotspot points layer on a flight
    hotspotsPointLayerFeature: boolean

    // true - enables toggling heatmap layer on a flight
    hotspotsHeatMapLayerFeature: boolean

    // true - enables toggling hotspot shape layer on a flight
    hotspotsShapeLayerFeature: boolean

    // true - enables toggling hotspot number layer on a flight
    hotspotsLabelLayerFeature: boolean

    // true - enables uploading of all infrared data
    allowUploadInfraredFeature: boolean

    // true - enables uploading of infrared data in tif/tiff format
    allowUploadTiffFeature: boolean

    // true - enables uploading of infrared data in jpg/jpeg format
    allowUploadJpgFeature: boolean

    // true - shows the profile icon on the right side of the map
    showProfileMapControl: boolean

    // true - skips kanban board if a fireId is set in rehydration via localstorage
    skipInitialKanban: boolean

    // true - shows the federated buttons (Google, Apple, Microsoft) for logging in and registering
    showFederatedIcons: boolean

    // true - hides the tabs while we only have icons available (no lines or polygons)
    hideIconPanelTabs: boolean

    // true - shows checkbox to enable mailchimp integration
    enableMailchimpIntegration: boolean

    // true - shows the new version of the bottom panel that is currently WIP
    useBottomPanelRevision: boolean

    // true - enables orthomosaic plugin UI
    useOrthomosaicUI: boolean

    // true - enables deletion for portfolio items
    usePortfolioDeletion: boolean

    // true - enables the hotspot api endpoints
    useHotspotAPIEndpoint: boolean

    // true - enables the flight path api endpoints
    useFlightpathAPIEndpoint: boolean

    // true - enables the fire perimeter api endpoints
    useFirePerimeterAPIEndpoint: boolean

    // true - enables unsplash photos
    useUnsplashPhotos: boolean

    // true - enables PDF generation
    usePDFGeneration: boolean

    //true - enables fire perimeter plugin
    useFirePerimeterPlugin: boolean

    //true - enables the new image preview modal for hotspots
    useImagePreviewModal: boolean

    /**
     * DEPRECATED
     *
     * See `merged.ts` for how to do this moving forward
     */
}

// IMPORTANT: because the way nextjs compiles images (npm run build), the "development" config will
// only actually be picked up when we use "npm run dev"
const development: ApplicationConfigurationFlags = {
    autoSelectRecentChapter: true,
    hexMapFeature: true,
    rawDataExportFeature: true,
    orthomosaicFeature: true,
    flightPathFeature: true,
    hotspotsPointLayerFeature: true,
    hotspotsHeatMapLayerFeature: true,
    hotspotsShapeLayerFeature: false,
    hotspotsLabelLayerFeature: false,
    allowUploadInfraredFeature: true,
    allowUploadTiffFeature: true,
    allowUploadJpgFeature: true,
    showProfileMapControl: false,
    skipInitialKanban: false,
    showFederatedIcons: false,
    hideIconPanelTabs: true,
    useBottomPanelRevision: true,
    enableMailchimpIntegration: true,
    useOrthomosaicUI: true,
    usePortfolioDeletion: true,
    useHotspotAPIEndpoint: true,
    useFlightpathAPIEndpoint: true,
    useUnsplashPhotos: true,
    usePDFGeneration: true,
    useFirePerimeterAPIEndpoint: true,
    useFirePerimeterPlugin: true,
    useImagePreviewModal: true,

    /**
     * DEPRECATED
     *
     * See `merged.ts` for how to do this moving forward
     */
}

// for all others, including development.app.fireai.io, this production set of flags will be used:
const production: ApplicationConfigurationFlags = {
    autoSelectRecentChapter: true,
    hexMapFeature: false,
    rawDataExportFeature: false,
    orthomosaicFeature: false,
    flightPathFeature: true,
    hotspotsPointLayerFeature: true,
    hotspotsHeatMapLayerFeature: true,
    hotspotsShapeLayerFeature: false,
    hotspotsLabelLayerFeature: false,
    allowUploadInfraredFeature: true,
    allowUploadTiffFeature: true,
    allowUploadJpgFeature: true,
    showProfileMapControl: false,
    skipInitialKanban: false,
    showFederatedIcons: false,
    hideIconPanelTabs: true,
    useBottomPanelRevision: true,
    enableMailchimpIntegration: true,
    useOrthomosaicUI: true,
    usePortfolioDeletion: true,
    useHotspotAPIEndpoint: true,
    useFlightpathAPIEndpoint: true,
    useUnsplashPhotos: true,
    usePDFGeneration: true,
    useFirePerimeterAPIEndpoint: true,
    useFirePerimeterPlugin: true,
    useImagePreviewModal: true,

    /**
     * DEPRECATED
     *
     * See `merged.ts` for how to do this moving forward
     */
}

const active = process.env.NODE_ENV === "production" ? production : development

export default active
